/**
 * ConversationTaskList
 *
 * Compact task list rendered below the Chain-of-Thought. Presentational only —
 * it takes a derived {@link ConversationTask}[] (see `cot-task-derive.ts`) and
 * renders each task with a minimal, monochrome status treatment that matches
 * the CoT timeline aesthetic (foreground-opacity, not the colored status icons
 * the canvas `AgentTaskItem` uses):
 *
 *   - open (pending)    → gray text
 *   - working (running) → white text, pulsing (shared `animate-cot-breathe`)
 *   - done (completed)  → gray text, struck through
 *
 * `blocked` / `failed` get minimal muted fallbacks.
 */

import type { LucideIcon } from 'lucide-react';
import { Circle, Loader2, Check, Ban, AlertCircle } from 'lucide-react';
import { AnimatePresence } from 'motion/react';
import { AnimatedListItem } from '../ui/AnimatedListItem';
import type { ConversationTask, ConversationTaskStatus } from './cot-task-derive';
import { cleanDisplayText } from '@/utils/display-text';

interface StatusStyle {
  icon: LucideIcon;
  /** Extra classes for the icon (after the shared size/shrink classes). */
  iconClass: string;
  /** Classes for the title text. */
  textClass: string;
}

const STATUS_STYLE: Record<ConversationTaskStatus, StatusStyle> = {
  pending: {
    icon: Circle,
    iconClass: 'text-muted-foreground/40',
    textClass: 'text-muted-foreground',
  },
  running: {
    icon: Loader2,
    iconClass: 'text-foreground animate-spin',
    textClass: 'font-semibold text-foreground animate-cot-breathe',
  },
  completed: {
    icon: Check,
    iconClass: 'text-muted-foreground/40',
    textClass: 'text-muted-foreground line-through decoration-muted-foreground/50',
  },
  blocked: {
    icon: Ban,
    iconClass: 'text-muted-foreground/40',
    textClass: 'text-muted-foreground',
  },
  failed: {
    icon: AlertCircle,
    iconClass: 'text-muted-foreground/40',
    textClass: 'text-muted-foreground',
  },
};

function TaskRow({ task }: { task: ConversationTask }) {
  const style = STATUS_STYLE[task.status];
  const Icon = style.icon;
  return (
    <div className="flex items-center gap-2 py-0.5 text-sm">
      <Icon className={`h-3.5 w-3.5 shrink-0 ${style.iconClass}`} />
      <span className={`truncate ${style.textClass}`}>{cleanDisplayText(task.title)}</span>
      {task.status === 'running' && task.activeForm && (
        <span className="truncate text-xs italic text-muted-foreground/70">
          {task.activeForm}
        </span>
      )}
    </div>
  );
}

export function ConversationTaskList({ tasks }: { tasks: ConversationTask[] }) {
  if (tasks.length === 0) return null;

  return (
    <div className="flex flex-col gap-0.5" data-testid="conversation-task-list">
      <AnimatePresence initial={false}>
        {tasks.map((task) => (
          <AnimatedListItem key={task.taskId}>
            <TaskRow task={task} />
          </AnimatedListItem>
        ))}
      </AnimatePresence>
    </div>
  );
}
